import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import type { PropsWithChildren, ReactNode } from 'react';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';

interface OverlayContextInterface {
  isOpen: boolean;
  content: ReactNode | null;
  isLoading: boolean;
  openOverlay: (content: ReactNode) => void;
  closeOverlay: () => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const OVERLAY_CONTEXT_DEFAULT_VALUE: OverlayContextInterface = {
  isOpen: false,
  content: null,
  isLoading: false,
  openOverlay: () => null,
  closeOverlay: () => null,
  showLoading: () => null,
  hideLoading: () => null,
};

const OverlayContext = createContext<OverlayContextInterface>(
  OVERLAY_CONTEXT_DEFAULT_VALUE,
);

export const useOverlay = () => useContext(OverlayContext);

export const OverlayProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [content, setContent] = useState<ReactNode | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const openOverlay = useCallback((content: ReactNode) => {
    setContent(content);
    setIsOpen(true);
  }, []);

  const closeOverlay = useCallback(() => {
    setIsOpen(false);
    // コンテンツはアニメーション後にクリアするのが良いですが、
    // 簡略化のため即時クリアします
    setContent(null);
  }, []);

  const showLoading = useCallback(() => {
    setIsLoading(true);
  }, []);

  const hideLoading = useCallback(() => {
    setIsLoading(false);
  }, []);

  // 背景クリックでオーバーレイを閉じる
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      // contentRef.currentをクリックした場合は閉じない
      if (contentRef.current && contentRef.current.contains(e.target as Node)) {
        return;
      }
      closeOverlay();
    },
    [closeOverlay],
  );

  const contextValue = {
    isOpen,
    content,
    isLoading,
    openOverlay,
    closeOverlay,
    showLoading,
    hideLoading,
  };

  return (
    <OverlayContext.Provider value={contextValue}>
      {children}

      {/* コンテンツ表示用オーバーレイ */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}
        open={isOpen}
        onClick={handleBackdropClick}
      >
        {content && (
          <Paper
            ref={contentRef}
            elevation={3}
            sx={{
              p: 3,
              maxWidth: '90%',
              maxHeight: '90%',
              overflow: 'auto',
              borderRadius: 2,
            }}
          >
            {content}
          </Paper>
        )}
      </Backdrop>

      {/* ローディング表示用オーバーレイ */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 2,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        }}
        open={isLoading}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <CircularProgress color='inherit' />
        </Box>
      </Backdrop>
    </OverlayContext.Provider>
  );
};
